// Library imports
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as THREE from "three";
import Lottie from "react-lottie";
import * as animationData from "../components/assets/dragmoveAnimation.json";
import { useFrame, useLoader, useThree } from "@react-three/fiber";

// App library imports
import AppCanvas from "../appComponents/viewer/AppCanvas";
import AppCamera from "../appComponents/viewer/AppCamera";
import AppControls from "../appComponents/viewer/AppControls";
import AppCube from "../appComponents/viewer/AppCube";

// Import models
import { IRootState } from "../models/general.model";
import { IScene, ISceneImageOverlay, ISceneMovement } from "../models/scene.model";
import { Html } from "@react-three/drei";
import {
  CircularProgress,
  Grid,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { Movement } from "./Movement";
import {
  hotspotType,
  HotspotVideoWall,
  IHotspot,
} from "../models/hotspot.model";
import { Information } from "./Information";
import React from "react";
import { InformationProduct } from "./InformationProduct";
import ProductInformation from "../components/layouts/ProductInformationModal";
import InformationModal from "../components/layouts/InformationModal";
import { getImages } from "../helpers/Image.helper";
import { getConfig } from "../store/thunks/config.thunk";
import { AppDispatch } from "../store/store";
import Arrow from "./Arrow";
import appTheme from "../styles/theme";
import ThemeProvider from "@mui/material/styles/ThemeProvider";
import { ComingSoon } from "./ComingSoon";
import numberToSide from "../helpers/Side.helper";
import ComingSoonModal from "../components/layouts/ComingSoonModal";
import WelcomeScreen from "./WelcomeScreen";
import WelcomeStart from "./WelcomeStart";
import AppPrimaryButton from "../appComponents/buttons/AppPrimaryButton";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import TransparentVideoOverlay, {
  ITransparentVideoOverlayHandle,
} from "./TransparentVideoOverlay";

const MOVEMENT_VIDEO_BASE_URL =
  "https://virtus-tech-media-api-prod.s3.eu-west-1.amazonaws.com/virtual-bd/immersive-movement";
const CUBE_SIDE_INDEXES = [0, 1, 2, 3, 4, 5] as const;
const CUBE_WALL_OFFSET = 249.6;
const CAMERA_LOOKAT_LERP_ALPHA = 0.12;
const CAMERA_LOOKAT_EPSILON = 0.002;
interface ITransparentSceneVideoConfig {
  url: string;
  corner: "center" | "bottom-right";
  desktop: {
    height: number;
    marginX: number;
    marginY: number;
  };
  mobile: {
    height: number;
    marginX: number;
    marginY: number;
  };
}

const TRANSPARENT_SCENE_VIDEO_CONFIG: Record<
  string,
  ITransparentSceneVideoConfig
> = {
  "1": {
    url: "https://virtus-tech-media-api-prod.s3.eu-west-1.amazonaws.com/virtual-bd/videos/intro.mp4",
    corner: "bottom-right",
    desktop: {
      height: 4.9,
      marginX: -0.9,
      marginY: 0,
    },
    mobile: {
      height: 2.8,
      marginX: -0.8,
      marginY: 0,
    },
  },
  "2": {
    url: "https://virtus-tech-media-api-prod.s3.eu-west-1.amazonaws.com/virtual-bd/videos/person-3.mp4",
    corner: "bottom-right",
    desktop: {
      height: 2.9,
      marginX: -0.9,
      marginY: -0.4,
    },
    mobile: {
      height: 2.8,
      marginX: -0.8,
      marginY: 0,
    },
  },
  "3": {
    url: "https://virtus-tech-media-api-prod.s3.eu-west-1.amazonaws.com/virtual-bd/videos/person-2.mp4",
    corner: "bottom-right",
    desktop: {
      height: 2.9,
      marginX: -0.9,
      marginY: -0.4,
    },
    mobile: {
      height: 2.8,
      marginX: -0.8,
      marginY: 0,
    },
  },
};
const GUIDED_SCENE_STEPS = [
  { id: "1", label: "Welcome" },
  { id: "2", label: "Easy Load" },
  { id: "3", label: "Single Load" },
] as const;

function configureVideoElement(video: HTMLVideoElement) {
  video.crossOrigin = "anonymous";
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.loop = false;
  video.preload = "auto";
  video.autoplay = true;
  video.setAttribute("playsinline", "true");
  video.setAttribute("webkit-playsinline", "true");
}

function configureVideoTexture(texture: THREE.VideoTexture) {
  texture.encoding = THREE.sRGBEncoding;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.needsUpdate = true;
}

function buildMovementVideoUrls(movementKey: string): string[] {
  const movementPath = movementKey.trim().replace(/\s+/g, "+");

  return CUBE_SIDE_INDEXES.map(
    (side) =>
      `${MOVEMENT_VIDEO_BASE_URL}/${movementPath}/${numberToSide(side)}.mp4`,
  );
}

function getArrowVideoOverlayPlacement(
  position?: {
    x: number;
    y: number;
    z: number;
  },
  explicitWall?: HotspotVideoWall,
) {
  if (explicitWall === "right") {
    return {
      wallPosition: [CUBE_WALL_OFFSET, 0, 0] as [number, number, number],
      wallRotation: [0, -Math.PI / 2, 0] as [number, number, number],
    };
  }

  if (explicitWall === "left") {
    return {
      wallPosition: [-CUBE_WALL_OFFSET, 0, 0] as [number, number, number],
      wallRotation: [0, Math.PI / 2, 0] as [number, number, number],
    };
  }

  if (explicitWall === "front") {
    return {
      wallPosition: [0, 0, -CUBE_WALL_OFFSET] as [number, number, number],
      wallRotation: [0, 0, 0] as [number, number, number],
    };
  }

  if (explicitWall === "back") {
    return {
      wallPosition: [0, 0, CUBE_WALL_OFFSET] as [number, number, number],
      wallRotation: [0, Math.PI, 0] as [number, number, number],
    };
  }

  if (!position) {
    return {
      wallPosition: [0, 0, -CUBE_WALL_OFFSET] as [number, number, number],
      wallRotation: [0, 0, 0] as [number, number, number],
    };
  }

  if (Math.abs(position.x) >= Math.abs(position.z)) {
    if (position.x >= 0) {
      return {
        wallPosition: [CUBE_WALL_OFFSET, 0, 0] as [number, number, number],
        wallRotation: [0, -Math.PI / 2, 0] as [number, number, number],
      };
    }

    return {
      wallPosition: [-CUBE_WALL_OFFSET, 0, 0] as [number, number, number],
      wallRotation: [0, Math.PI / 2, 0] as [number, number, number],
    };
  }

  if (position.z >= 0) {
    return {
      wallPosition: [0, 0, CUBE_WALL_OFFSET] as [number, number, number],
      wallRotation: [0, Math.PI, 0] as [number, number, number],
    };
  }

  return {
    wallPosition: [0, 0, -CUBE_WALL_OFFSET] as [number, number, number],
    wallRotation: [0, 0, 0] as [number, number, number],
  };
}

function VideoCubeTransition({
  urls,
  onReady,
  onEnded,
  onError,
}: {
  urls: string[];
  onReady: () => void;
  onEnded: () => void;
  onError: () => void;
}) {
  const [ready, setReady] = useState(false);
  const texturesRef = useRef<THREE.VideoTexture[]>([]);
  const materialsRef = useRef<THREE.MeshBasicMaterial[] | null>(null);

  const onReadyRef = useRef(onReady);
  const onEndedRef = useRef(onEnded);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let cancelled = false;
    let endedCount = 0;
    let failed = false;
    let playbackStarted = false;

    setReady(false);

    const videos: HTMLVideoElement[] = [];
    const textures: THREE.VideoTexture[] = [];
    const materials: THREE.MeshBasicMaterial[] = [];
    const readyVideos = new Set<HTMLVideoElement>();

    const failTransition = (message: string, error?: unknown) => {
      if (failed || cancelled) return;

      failed = true;
      if (error) {
        console.warn(message, error);
      } else {
        console.warn(message);
      }
      onErrorRef.current();
    };

    const tryStartPlayback = () => {
      if (playbackStarted || failed || cancelled) return;
      if (readyVideos.size !== videos.length || videos.length === 0) return;

      playbackStarted = true;

      Promise.all(
        videos.map((video) => {
          video.currentTime = 0;
          const playPromise = video.play();
          return playPromise === undefined
            ? Promise.resolve()
            : playPromise.catch((error) => {
                throw error;
              });
        }),
      )
        .then(() => {
          if (cancelled) return;
          setReady(true);
          onReadyRef.current();
        })
        .catch((error) => {
          failTransition("Could not start movement transition videos:", error);
        });
    };

    const markVideoReady = (video: HTMLVideoElement | null) => {
      if (!video || failed || cancelled || readyVideos.has(video)) return;
      if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return;
      readyVideos.add(video);
      tryStartPlayback();
    };

    const handleCanPlay = (event: Event) => {
      markVideoReady(event.currentTarget as HTMLVideoElement | null);
    };

    const handleEnded = () => {
      endedCount += 1;
      if (endedCount === videos.length && !cancelled && !failed) {
        onEndedRef.current();
      }
    };

    const handleError = () => {
      failTransition("A movement transition video failed to load.");
    };

    urls.forEach((url) => {
      const video = document.createElement("video");
      configureVideoElement(video);
      video.src = url;

      const texture = new THREE.VideoTexture(video);
      configureVideoTexture(texture);

      const material = new THREE.MeshBasicMaterial({
        map: texture,
        toneMapped: false,
      });

      video.addEventListener("loadeddata", handleCanPlay);
      video.addEventListener("canplay", handleCanPlay);
      video.addEventListener("canplaythrough", handleCanPlay);
      video.addEventListener("ended", handleEnded);
      video.addEventListener("error", handleError);

      videos.push(video);
      textures.push(texture);
      materials.push(material);

      video.load();
      markVideoReady(video);
    });

    texturesRef.current = textures;
    materialsRef.current = materials;

    return () => {
      cancelled = true;
      setReady(false);

      videos.forEach((video) => {
        video.removeEventListener("loadeddata", handleCanPlay);
        video.removeEventListener("canplay", handleCanPlay);
        video.removeEventListener("canplaythrough", handleCanPlay);
        video.removeEventListener("ended", handleEnded);
        video.removeEventListener("error", handleError);
        video.pause();
        video.src = "";
      });

      materials.forEach((material) => {
        material.map?.dispose();
        material.dispose();
      });

      texturesRef.current = [];
      materialsRef.current = null;
    };
  }, [urls]);

  useFrame(() => {
    texturesRef.current.forEach((texture) => {
      const video = texture.image as HTMLVideoElement;
      if (video && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        texture.needsUpdate = true;
      }
    });
  });

  if (!ready || !materialsRef.current) return null;

  return (
    <mesh material={materialsRef.current}>
      <boxGeometry args={[500, 500, -500]} />
    </mesh>
  );
}

function FrontFaceVideoOverlay({
  url,
  onReady,
  onEnded,
  onError,
  wallPosition,
  wallRotation,
}: {
  url: string;
  onReady: () => void;
  onEnded: () => void;
  onError: () => void;
  wallPosition: [number, number, number];
  wallRotation: [number, number, number];
}) {
  const [ready, setReady] = useState(false);
  const textureRef = useRef<THREE.VideoTexture | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const onReadyRef = useRef(onReady);
  const onEndedRef = useRef(onEnded);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let cancelled = false;
    let playbackRequested = false;
    setReady(false);

    const video = document.createElement("video");
    configureVideoElement(video);
    video.src = url;
    videoRef.current = video;

    const startPlayback = () => {
      if (cancelled || playbackRequested) return;
      if (video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) return;

      playbackRequested = true;
      video.currentTime = 0;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn("Could not play front-side overlay video:", error);
          onErrorRef.current();
        });
      }
    };

    const handleCanPlay = () => {
      startPlayback();
    };

    const handlePlaying = () => {
      if (cancelled) return;

      if (!textureRef.current) {
        const texture = new THREE.VideoTexture(video);
        configureVideoTexture(texture);
        textureRef.current = texture;
      }

      setReady(true);
      onReadyRef.current();
    };

    const handleEnded = () => {
      if (cancelled) return;
      onEndedRef.current();
    };

    const handleError = () => {
      if (cancelled) return;
      onErrorRef.current();
    };

    video.addEventListener("loadeddata", handleCanPlay);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("canplaythrough", handleCanPlay);
    video.addEventListener("playing", handlePlaying);
    video.addEventListener("ended", handleEnded);
    video.addEventListener("error", handleError);

    video.load();
    startPlayback();

    return () => {
      cancelled = true;
      video.removeEventListener("loadeddata", handleCanPlay);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("canplaythrough", handleCanPlay);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("ended", handleEnded);
      video.removeEventListener("error", handleError);
      video.pause();
      video.src = "";

      if (textureRef.current) {
        textureRef.current.dispose();
        textureRef.current = null;
      }
      videoRef.current = null;
      setReady(false);
    };
  }, [url]);

  useFrame(() => {
    const texture = textureRef.current;
    const video = videoRef.current;
    if (
      texture &&
      video &&
      video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
    ) {
      texture.needsUpdate = true;
    }
  });

  if (!ready || !textureRef.current) return null;

  return (
    <mesh position={wallPosition} rotation={wallRotation}>
      <planeGeometry args={[500, 500]} />
      <meshBasicMaterial
        map={textureRef.current}
        side={THREE.DoubleSide}
        toneMapped={false}
      />
    </mesh>
  );
}

function SceneImageOverlay({
  overlay,
}: {
  overlay: ISceneImageOverlay;
}) {
  const texture = useLoader(THREE.TextureLoader, overlay.url);

  useEffect(() => {
    texture.encoding = THREE.sRGBEncoding;
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
  }, [texture]);

  const imageAspectRatio = useMemo(() => {
    const sourceImage = texture.image as
      | HTMLImageElement
      | HTMLCanvasElement
      | undefined;

    if (!sourceImage?.width || !sourceImage?.height) return 1;
    return sourceImage.width / sourceImage.height;
  }, [texture]);

  const planeWidth = overlay.height * imageAspectRatio;

  return (
    <mesh
      position={[overlay.position.x, overlay.position.y, overlay.position.z]}
      rotation={[overlay.rotation.x, overlay.rotation.y, overlay.rotation.z]}
      renderOrder={14}
    >
      <planeGeometry args={[planeWidth, overlay.height]} />
      <meshBasicMaterial
        map={texture}
        side={THREE.DoubleSide}
        toneMapped={false}
        transparent={true}
      />
    </mesh>
  );
}

function SceneCameraReset({
  resetKey,
  cameraPosition,
  lookAt,
}: {
  resetKey: string;
  cameraPosition: [number, number, number];
  lookAt: [number, number, number];
}) {
  const { camera, controls } = useThree((state: any) => ({
    camera: state.camera as THREE.PerspectiveCamera,
    controls: state.controls,
  }));

  const animatingRef = useRef<boolean>(false);
  const startLookAtRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const targetLookAtRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const lookDirectionRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const currentLookAtRef = useRef<THREE.Vector3>(new THREE.Vector3());

  useFrame(() => {
    if (!animatingRef.current) return;
    currentLookAtRef.current.lerp(
      targetLookAtRef.current,
      CAMERA_LOOKAT_LERP_ALPHA,
    );

    const orbitControls = controls as any;
    if (orbitControls?.target?.copy) {
      orbitControls.target.copy(currentLookAtRef.current);
      orbitControls.update?.();
    } else {
      camera.lookAt(currentLookAtRef.current);
    }

    if (
      currentLookAtRef.current.distanceTo(targetLookAtRef.current) <=
      CAMERA_LOOKAT_EPSILON
    ) {
      currentLookAtRef.current.copy(targetLookAtRef.current);
      if (orbitControls?.target?.copy) {
        orbitControls.target.copy(targetLookAtRef.current);
        orbitControls.update?.();
      } else {
        camera.lookAt(targetLookAtRef.current);
      }
      animatingRef.current = false;
    }
  });

  return null;
}

interface IActiveTransition {
  movementKey: string;
  targetSceneId: string;
}

interface IActiveArrowVideo {
  hotspotId: string;
  url: string;
  wallPosition: [number, number, number];
  wallRotation: [number, number, number];
}

export default function Viewer() {
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const [allSidesLoaded, setAllSidesLoaded] = useState(false);
  const [guided, setGuided] = useState<boolean>(false);
  const { sceneId }: any = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [activeTransition, setActiveTransition] =
    useState<IActiveTransition | null>(null);
  const [movementVideoLoading, setMovementVideoLoading] =
    useState<boolean>(false);
  const [movementVideoReady, setMovementVideoReady] = useState<boolean>(false);
  const [activeArrowVideo, setActiveArrowVideo] =
    useState<IActiveArrowVideo | null>(null);
  const [arrowVideoLoading, setArrowVideoLoading] = useState<boolean>(false);
  const [arrowVideoPlaying, setArrowVideoPlaying] = useState<boolean>(false);
  const transparentVideoRef = useRef<ITransparentVideoOverlayHandle | null>(
    null,
  );
  const [transparentSceneAudioEnabled, setTransparentSceneAudioEnabled] =
    useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  // useSelector allows you to access the data from the slice
  const { config, id } = useSelector((state: IRootState) => state.ConfigSlice);

  const [currentScene, setCurrentScene] = useState<IScene>({} as IScene);
  const [currentContent, setCurrentContent] = useState<any>(undefined);

  const sceneMovements = currentScene?.movements ?? [];
  const activeTransparentSceneVideoConfig =
    sceneId && TRANSPARENT_SCENE_VIDEO_CONFIG[sceneId]
      ? TRANSPARENT_SCENE_VIDEO_CONFIG[sceneId]
      : null;
  const guidedSceneIndex = GUIDED_SCENE_STEPS.findIndex(
    (step) => step.id === sceneId,
  );
  const isGuidedScene = guidedSceneIndex !== -1;
  const activeMovementUrls = useMemo(
    () =>
      activeTransition?.movementKey
        ? buildMovementVideoUrls(activeTransition.movementKey)
        : [],
    [activeTransition],
  );

  const [productOpen, setProductOpen] = useState<boolean>(false);
  const [infoOpen, setInfoOpen] = useState<boolean>(false);
  const [coningSoonOpen, setComingSoonOpen] = useState<boolean>(false);

  const [demo, setDemo] = useState<boolean>(false);

  useEffect(() => {
    const viewport = document.querySelector('meta[name="viewport"]');

    if (viewport) {
      viewport.setAttribute("content", "width=1024");
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("inactivityRedirectEnabled") === "true") {
      setDemo(true);
    } else {
      setDemo(false);
    }
  }, []);

  const [currentTexture, setCurrentTexture] = useState<
    THREE.MeshBasicMaterial[] | undefined
  >(undefined);

  const handleMouseMove = useCallback((e: any) => {
    // left in here if you re-enable the drawer later
  }, []);

  // Lottie animation state
  const [showDragAnimation, setShowDragAnimation] = useState(false);
  const [userHasDragged, setUserHasDragged] = useState(false);

  // Show Lottie animation when first scene loads
  const lottieAnimationHasRanOnce = useRef<boolean>(false);
  useEffect(() => {
    if (
      currentScene &&
      currentScene.id === "5" &&
      !lottieAnimationHasRanOnce.current
    ) {
      lottieAnimationHasRanOnce.current = true;
      setShowDragAnimation(false);
      setUserHasDragged(false);
      setTimeout(() => setShowDragAnimation(true), 1000);
      setTimeout(() => setShowDragAnimation(false), 4000);
    }
  }, [currentScene]);

  useEffect(() => {
    if (coningSoonOpen) {
      setShowDragAnimation(false);
    }
  }, [coningSoonOpen]);

  // Inactivity timer for auto-redirect
  useEffect(() => {
    if (demo) {
      let timeout: NodeJS.Timeout | null = null;
      const resetTimer = () => {
        if (timeout) clearTimeout(timeout);
        if (!infoOpen && !productOpen && !coningSoonOpen) {
          timeout = setTimeout(() => {
            navigate("/");
          }, 50000);
        }
      };
      // Listen for user activity
      const events = [
        "mousemove",
        "mousedown",
        "keydown",
        "touchstart",
        "scroll",
      ];

      events.forEach((event) => window.addEventListener(event, resetTimer));

      resetTimer();
      return () => {
        if (timeout) clearTimeout(timeout);
        events.forEach((event) =>
          window.removeEventListener(event, resetTimer),
        );
      };
    }
  }, [infoOpen, productOpen, coningSoonOpen, navigate]);

  /**
   * Load static cube textures (no video textures here)
   */
  useEffect(() => {
    if (!currentScene || !currentScene.image) return;

    setAllSidesLoaded(false);

    const loader = new THREE.TextureLoader();
    let cancelled = false;
    let loadedCount = 0;

    const handleSideLoaded = () => {
      loadedCount++;
      if (loadedCount === 6) {
        setAllSidesLoaded(true);
      }
    };

    const loadSide = (sideIndex: number) => {
      const tex = loader.load(
        `https://virtus-tech-media-api-prod.s3.eu-west-1.amazonaws.com/virtual-bd/immersive-images/${
          currentScene.image
        }/${numberToSide(sideIndex)}.jpg`,
        handleSideLoaded,
      );
      tex.encoding = THREE.sRGBEncoding;
      return tex;
    };

    const rightTexture = loadSide(0);
    const leftTexture = loadSide(1);
    const upTexture = loadSide(2);
    const downTexture = loadSide(3);
    const frontTexture = loadSide(4);
    const backTexture = loadSide(5);

    const texturebuffer = [
      new THREE.MeshBasicMaterial({ map: rightTexture }),
      new THREE.MeshBasicMaterial({ map: leftTexture }),
      new THREE.MeshBasicMaterial({ map: upTexture }),
      new THREE.MeshBasicMaterial({ map: downTexture }),
      new THREE.MeshBasicMaterial({ map: frontTexture }),
      new THREE.MeshBasicMaterial({ map: backTexture }),
    ];

    if (!cancelled) setCurrentTexture(texturebuffer);

    return () => {
      cancelled = true;
      texturebuffer.forEach((mat) => {
        mat.map?.dispose();
        mat.dispose();
      });
    };
  }, [currentScene]);

  // Set currentScene based on URL + config
  useEffect(() => {
    if (sceneId !== currentScene.id) {
      const foundScene: IScene | undefined = config?.scenes?.find(
        (scene: IScene) => scene.id === sceneId,
      );
      if (foundScene) {
        setCurrentScene(foundScene);
      }
    }
    if (config?.id && !currentScene.textures) {
      const foundScene: IScene | undefined = config?.scenes?.find(
        (scene: IScene) => scene.id === sceneId,
      );

      if (
        foundScene &&
        foundScene.textures &&
        foundScene.textures.length !== 0
      ) {
        setCurrentScene(foundScene);
        setLoading(false);
      }
    }
  }, [sceneId, config]);

  useEffect(() => {
    setMovementVideoLoading(false);
    setMovementVideoReady(false);
    setActiveTransition(null);
    setArrowVideoLoading(false);
    setArrowVideoPlaying(false);
    setActiveArrowVideo(null);
  }, [sceneId]);

  useEffect(() => {
    setTransparentSceneAudioEnabled(false);
  }, [sceneId]);

  useEffect(() => {
    // If data is available start loading images
    if (id !== "") {
      getImages(config, dispatch);
    }
  }, [id, config, dispatch]);

  useEffect(() => {
    // Sending request to get data
    dispatch(getConfig());
  }, [dispatch]);

  const [clicked, setClicked] = useState<string>("");

  function clickedOpen(hotspot: any) {
    setInfoOpen(true);
    setCurrentContent(hotspot.action);
  }

  function clickedPOpen(hotspot: any) {
    setProductOpen(true);
    setCurrentContent(hotspot.action);
  }

  function clickedCSOpen(hotspot: any) {
    setComingSoonOpen(true);
    setCurrentContent(hotspot.action);
  }

  const handleArrowVideoReady = useCallback(() => {
    setArrowVideoLoading(false);
    setArrowVideoPlaying(true);
  }, []);

  const handleArrowVideoEnded = useCallback(() => {
    setArrowVideoLoading(false);
    setArrowVideoPlaying(false);
    setActiveArrowVideo(null);
  }, []);

  const handleArrowVideoError = useCallback(() => {
    setArrowVideoLoading(false);
    setArrowVideoPlaying(false);
    setActiveArrowVideo(null);
  }, []);

  const handleMovementPlaybackReady = useCallback(() => {
    setMovementVideoLoading(false);
    setMovementVideoReady(true);
  }, []);

  const handleMovementPlaybackError = useCallback(() => {
    setMovementVideoLoading(false);
    setMovementVideoReady(false);
    setActiveTransition(null);
  }, []);

  const handleMovementPlaybackEnded = useCallback(() => {
    const completedTransition = activeTransition;

    setMovementVideoLoading(false);
    setMovementVideoReady(false);
    setActiveTransition(null);

    if (!completedTransition || !config?.scenes) return;

    const targetScene = config.scenes.find(
      (scene: IScene) => scene.id === completedTransition.targetSceneId,
    );

    if (targetScene) {
      setCurrentScene(targetScene);
      navigate(`/virtual/${completedTransition.targetSceneId}`);
    }
  }, [activeTransition, config, navigate]);

  const startSceneMovement = useCallback(
    (movement?: ISceneMovement) => {
      if (activeTransition || activeArrowVideo) return;

      const selectedMovement = movement ?? sceneMovements[0];
      if (!selectedMovement?.movementKey) return;

      setMovementVideoLoading(true);
      setMovementVideoReady(false);
      setActiveTransition({
        movementKey: selectedMovement.movementKey,
        targetSceneId: selectedMovement.target,
      });
    },
    [activeArrowVideo, activeTransition, sceneMovements],
  );

  const startArrowSideVideo = useCallback((hotspot: IHotspot) => {
    if (!hotspot.videoSrc) return;
    const overlayPlacement = getArrowVideoOverlayPlacement(
      hotspot.position,
      hotspot.videoWall,
    );
    setArrowVideoLoading(true);
    setArrowVideoPlaying(false);
    setActiveArrowVideo({
      hotspotId: hotspot.id,
      url: hotspot.videoSrc,
      wallPosition: overlayPlacement.wallPosition,
      wallRotation: overlayPlacement.wallRotation,
    });
  }, []);

  const isOverlayBusy =
    movementVideoLoading ||
    Boolean(activeTransition) ||
    arrowVideoLoading ||
    arrowVideoPlaying;
  const hideArrowHotspots = movementVideoLoading || Boolean(activeTransition);
  const showMovementButtons = !isGuidedScene;

  const getMovementButtonLabel = (
    movement: ISceneMovement,
    movementIndex: number,
  ) => {
    const isActiveMovement =
      activeTransition?.targetSceneId === movement.target &&
      activeTransition?.movementKey === movement.movementKey;

    if (movementVideoLoading && isActiveMovement) {
      return "Loading Transition...";
    }

    if (activeTransition && isActiveMovement) {
      return "Playing Transition...";
    }

    if (sceneMovements.length > 1) {
      return `Play Transition ${movementIndex + 1}`;
    }

    return "Play Transition";
  };

  const navigateToSceneWithTransition = useCallback(
    (targetSceneId: string) => {
      if (isOverlayBusy || currentScene?.id === targetSceneId) return;

      const targetMovement = sceneMovements.find(
        (movement: ISceneMovement) => movement.target === targetSceneId,
      );

      if (targetMovement?.movementKey) {
        startSceneMovement(targetMovement);
        return;
      }

      const targetScene = config?.scenes?.find(
        (scene: IScene) => scene.id === targetSceneId,
      );
      if (targetScene) {
        setCurrentScene(targetScene);
      }
      navigate(`/virtual/${targetSceneId}`);
    },
    [
      config,
      currentScene?.id,
      isOverlayBusy,
      navigate,
      sceneMovements,
      startSceneMovement,
    ],
  );

  const handleGuidedBack = useCallback(() => {
    if (!isGuidedScene || guidedSceneIndex < 0) return;
    const previousIndex =
      (guidedSceneIndex - 1 + GUIDED_SCENE_STEPS.length) %
      GUIDED_SCENE_STEPS.length;
    navigateToSceneWithTransition(GUIDED_SCENE_STEPS[previousIndex].id);
  }, [guidedSceneIndex, isGuidedScene, navigateToSceneWithTransition]);

  const handleGuidedNext = useCallback(() => {
    if (!isGuidedScene || guidedSceneIndex < 0) return;
    const nextIndex = (guidedSceneIndex + 1) % GUIDED_SCENE_STEPS.length;
    navigateToSceneWithTransition(GUIDED_SCENE_STEPS[nextIndex].id);
  }, [guidedSceneIndex, isGuidedScene, navigateToSceneWithTransition]);

  const handleGuidedStepSelect = useCallback(
    (targetSceneId: string) => {
      if (isOverlayBusy) return;
      navigateToSceneWithTransition(targetSceneId);
    },
    [isOverlayBusy, navigateToSceneWithTransition],
  );

  const handleTransparentSceneVideoAudioPlay = useCallback(async () => {
    if (transparentSceneAudioEnabled) return;

    const canPlayWithAudio = await transparentVideoRef.current?.playWithAudio();
    if (canPlayWithAudio) {
      setTransparentSceneAudioEnabled(true);
    }
  }, [transparentSceneAudioEnabled]);

  function handleRenderCanvas() {
    // Lottie options
    const lottieOptions = {
      loop: true,
      autoplay: true,
      animationData: animationData,
      rendererSettings: {
        preserveAspectRatio: "xMidYMid slice",
      },
    };

    // Get initialCameraPosition from currentScene, fallback to [0,0,0]
    const initialCameraPosition: [number, number, number] =
      currentScene &&
      currentScene.initialCameraPosition &&
      typeof currentScene.initialCameraPosition.x === "number" &&
      typeof currentScene.initialCameraPosition.y === "number" &&
      typeof currentScene.initialCameraPosition.z === "number"
        ? [
            currentScene.initialCameraPosition.x,
            currentScene.initialCameraPosition.y,
            currentScene.initialCameraPosition.z,
          ]
        : [0, 0.01, 0];
    const initialCameraLookAt: [number, number, number] = [0, 0, 0];

    return (
      <div style={{ position: "relative", width: "100%", height: "100%" }}>
        {/* Lottie Animation Overlay */}
        {currentScene && currentScene.id === "5" && showDragAnimation && (
          <div
            style={{
              pointerEvents: "none",
              zIndex: 100000,
              height: "160px",
              width: "160px",
              position: "absolute",
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              left: "calc(50vw - 80px)",
              top: "calc(50vh - 150px)",
              userSelect: "none",
              background: "#222",
              borderRadius: "20px",
              padding: "15px",
              opacity: 0.85,
              ...(isMobileDevice && {
                left: "calc(50vw - 180px)",
                top: "calc(50vh - 200px)",

                height: "360px",
                width: "360px",
              }),
            }}
          >
            <div
              style={{
                width: "95px",
                height: "95px",
                ...(isMobileDevice && { width: "195px", height: "195px" }),
              }}
            >
              <Lottie options={lottieOptions} />
            </div>
            <Typography
              variant={isMobileDevice ? "h4" : "caption"}
              align="center"
              sx={{ color: "#fff", fontWeight: "bold" }}
            >
              Click and drag to look around
            </Typography>
          </div>
        )}

        {/* Main Canvas */}
        <AppCanvas
          setClicked={setClicked}
          onPointerDown={() => {
            setUserHasDragged(true);
            if (
              currentScene?.id &&
              TRANSPARENT_SCENE_VIDEO_CONFIG[currentScene.id] &&
              !transparentSceneAudioEnabled
            ) {
              handleTransparentSceneVideoAudioPlay();
            }
          }}
        >
          <ThemeProvider theme={appTheme}>
            <AppCamera position={initialCameraPosition} />
            <AppControls />
            <SceneCameraReset
              resetKey={`${sceneId || currentScene?.id || ""}`}
              cameraPosition={initialCameraPosition}
              lookAt={initialCameraLookAt}
            />

            {/* Static 360 cube */}
            {!movementVideoReady &&
              (!loading && currentScene && currentTexture ? (
                <AppCube media={currentTexture} />
              ) : (
                <mesh>
                  <Html>
                    <CircularProgress />
                  </Html>
                </mesh>
              ))}

            {activeTransparentSceneVideoConfig &&
              !movementVideoLoading &&
              !activeTransition && (
                <TransparentVideoOverlay
                  ref={transparentVideoRef}
                  url={activeTransparentSceneVideoConfig.url}
                  corner={activeTransparentSceneVideoConfig.corner}
                  height={
                    isMobileDevice
                      ? activeTransparentSceneVideoConfig.mobile.height
                      : activeTransparentSceneVideoConfig.desktop.height
                  }
                  marginX={
                    isMobileDevice
                      ? activeTransparentSceneVideoConfig.mobile.marginX
                      : activeTransparentSceneVideoConfig.desktop.marginX
                  }
                  marginY={
                    isMobileDevice
                      ? activeTransparentSceneVideoConfig.mobile.marginY
                      : activeTransparentSceneVideoConfig.desktop.marginY
                  }
                />
              )}

            {/* Movement transition loading state */}
            {movementVideoLoading && !movementVideoReady && (
              <mesh>
                <Html center>
                  <div
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.7)",
                      color: "#fff",
                      borderRadius: "10px",
                      padding: isMobileDevice ? "24px 40px" : "16px 24px",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <CircularProgress color="inherit" />
                    <Typography
                      variant={isMobileDevice ? "h5" : "body1"}
                      sx={{ fontWeight: 600 }}
                    >
                      Loading transition...
                    </Typography>
                  </div>
                </Html>
              </mesh>
            )}

            {activeTransition && activeMovementUrls.length === 6 && (
              <VideoCubeTransition
                urls={activeMovementUrls}
                onReady={handleMovementPlaybackReady}
                onEnded={handleMovementPlaybackEnded}
                onError={handleMovementPlaybackError}
              />
            )}

            {activeArrowVideo && (
              <FrontFaceVideoOverlay
                url={activeArrowVideo.url}
                wallPosition={activeArrowVideo.wallPosition}
                wallRotation={activeArrowVideo.wallRotation}
                onReady={handleArrowVideoReady}
                onEnded={handleArrowVideoEnded}
                onError={handleArrowVideoError}
              />
            )}

            {currentScene?.imageOverlays?.map((overlay: ISceneImageOverlay) => (
              <SceneImageOverlay key={overlay.id} overlay={overlay} />
            ))}

            {/* Hotspots */}
            {currentScene &&
              currentScene.hotspots &&
              currentScene.hotspots.map((hotspot: IHotspot, index: number) => (
                <React.Fragment key={hotspot.id + index}>
                  {hotspot.type === "movement" && (
                    <Movement
                      hotspot={hotspot}
                      onHotspotClick={() => {
                        if (!allSidesLoaded) return;
                        const targetScene = config.scenes.find(
                          (scene: IScene) => scene.id === hotspot.action,
                        );
                        if (targetScene) {
                          setCurrentScene(targetScene);
                          navigate(`/virtual/${hotspot.action}`);
                        }
                      }}
                    />
                  )}

                  {hotspot.type === hotspotType.ARROW && !hideArrowHotspots && (
                    <Arrow
                      hotspot={hotspot}
                      machineLoading={isOverlayBusy}
                      onHotspotClick={() => {
                        if (hotspot.videoSrc) {
                          if (isOverlayBusy) return;
                          startArrowSideVideo(hotspot);
                          return;
                        }

                        if (currentScene?.movements?.length) {
                          startSceneMovement();
                          return;
                        }

                        const targetScene = config?.scenes?.find(
                          (scene: IScene) => scene.id === hotspot.action,
                        );

                        if (targetScene) {
                          setCurrentScene(targetScene);
                          navigate(`/virtual/${hotspot.action}`);
                        }
                      }}
                    />
                  )}

                  {hotspot.type === "info" && (
                    <Information
                      hotspot={hotspot}
                      clicked={clicked}
                      setClicked={setClicked}
                      clickedOpen={clickedOpen}
                    />
                  )}

                  {hotspot.type === "coming-soon" && (
                    <ComingSoon
                      hotspot={hotspot}
                      clicked={clicked}
                      setClicked={setClicked}
                      clickedOpen={clickedCSOpen}
                    />
                  )}

                  {hotspot.type === "info-product" && (
                    <InformationProduct
                      hotspot={hotspot}
                      clicked={clicked}
                      setClicked={setClicked}
                      clickedOpen={clickedPOpen}
                    />
                  )}
                </React.Fragment>
              ))}

            {/* Welcome overlays */}
            {currentScene.id === "5" && (
              <WelcomeScreen
                handleSceneChosen={(scene: number) => {
                  if (scene === 1) {
                    const targetScene = config.scenes.find(
                      (scene: IScene) => scene.id === "6",
                    );
                    if (targetScene) {
                      setCurrentScene(targetScene);
                      navigate("/virtual/6");
                    }
                  }

                  if (scene === 2) {
                    const targetScene = config.scenes.find(
                      (scene: IScene) => scene.id === "7",
                    );
                    if (targetScene) {
                      setCurrentScene(targetScene);
                      navigate("/virtual/7");
                    }
                  }
                }}
              />
            )}

            {currentScene.id === "6" && (
              <WelcomeStart
                title="Pharmacy"
                content="This example shows the journey of medicines in the pharmacy from delivery to the hospital through to the ward, and the use of BD Rowa. <br/> <br/>Click on the circular movement points on the floor to navigate through the journey, and on the orange information points to find out more about each stage."
                handleSceneChosen={(scene: number) => {
                  const targetScene = config.scenes.find(
                    (scene: IScene) => scene.id === "0",
                  );
                  if (targetScene) {
                    setCurrentScene(targetScene);
                    navigate("/virtual/0");
                  }
                }}
                location={new THREE.Vector3(-2, -0.9, -4)}
              />
            )}

            {currentScene.id === "7" && (
              <WelcomeStart
                title="Ward"
                content="This example shows the journey of patient care in a general ward, and the use of BD Pyxis for medication dispensing and BD Bactec for blood culture sampling. <br/> <br/> Click on the circular movement points on the floor to navigate through the journey, and on the orange information points to find out more about each stage."
                handleSceneChosen={(scene: number) => {
                  const targetScene = config.scenes.find(
                    (scene: IScene) => scene.id === "9",
                  );
                  if (targetScene) {
                    setCurrentScene(targetScene);
                    navigate("/virtual/9");
                  }
                }}
                location={new THREE.Vector3(3, -0.9, -2)}
              />
            )}
          </ThemeProvider>
        </AppCanvas>
      </div>
    );
  }

  return (
    <div style={{ width: "100vw", height: "100vh" }}>
      {guided ? (
        <>
          <Grid container sx={{ height: "100%" }}>
            <Grid
              item
              xs={12}
              lg={4}
              sx={{
                maxHeight: { sm: "50vh", md: "100%" },
              }}
            >
              <Box
                component="div"
                sx={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: "red",
                }}
              >
                test
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              lg={8}
              sx={{
                height: "100%",
                width: "100%",
              }}
            >
              {handleRenderCanvas()}
            </Grid>
            <Box
              component={"div"}
              sx={{
                height: "50px",
                backgroundColor: "green",
                position: "absolute",
                bottom: "0",
                left: "0",
                width: "100vw",
              }}
            ></Box>
          </Grid>
        </>
      ) : (
        <>{handleRenderCanvas()}</>
      )}

      <InformationModal
        open={infoOpen}
        setOpen={setInfoOpen}
        currentContent={currentContent}
      />

      <ProductInformation
        open={productOpen}
        setOpen={setProductOpen}
        currentContent={currentContent}
      />

      <ComingSoonModal
        open={coningSoonOpen}
        setOpen={setComingSoonOpen}
        currentContent={currentContent}
      />

      {isGuidedScene && (
        <Box
          component="div"
          sx={{
            position: "fixed",
            top: isMobileDevice ? 18 : 14,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            backgroundColor: "rgba(0, 86, 156, 0.92)",
            borderRadius: "999px",
            border: "1px solid rgba(255,255,255,0.35)",
            px: isMobileDevice ? 2 : 1.5,
            py: isMobileDevice ? 1.4 : 1,
          }}
        >
          <AppPrimaryButton
            size={isMobileDevice ? "large" : "medium"}
            disabled={isOverlayBusy}
            onClick={handleGuidedBack}
            style={{
              color: "white",
              border: "none",
              borderColor: "transparent",
              backgroundColor: "transparent",
              minWidth: isMobileDevice ? 140 : 100,
              fontSize: isMobileDevice ? "1.1rem" : "0.95rem",
            }}
          >
            Back
          </AppPrimaryButton>
          <Box
            component="div"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobileDevice ? 1 : 0.75,
              px: 0.2,
            }}
          >
            {GUIDED_SCENE_STEPS.map((step, index) => {
              const isActiveStep = guidedSceneIndex === index;

              return (
                <Box
                  key={step.id}
                  component="button"
                  type="button"
                  aria-label={`Go to ${step.label}`}
                  onClick={() => {
                    if (isActiveStep) return;
                    handleGuidedStepSelect(step.id);
                  }}
                  sx={{
                    borderRadius: "999px",
                    px: isMobileDevice ? 1.4 : 1.1,
                    py: isMobileDevice ? 0.7 : 0.5,
                    backgroundColor: isActiveStep
                      ? "rgba(255,255,255,0.95)"
                      : "rgba(168,168,168,0.65)",
                    minWidth: isMobileDevice ? 96 : 88,
                    border: "none",
                    cursor:
                      isActiveStep || isOverlayBusy ? "default" : "pointer",
                    opacity: isOverlayBusy && !isActiveStep ? 0.7 : 1,
                    transition: "background-color 0.2s ease, opacity 0.2s ease",
                    "&:focus-visible": {
                      outline: "2px solid #fff",
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: isActiveStep ? "#00569c" : "#f0f0f0",
                      fontWeight: 700,
                      textAlign: "center",
                      fontSize: isMobileDevice ? "0.95rem" : "0.85rem",
                      lineHeight: 1.1,
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          <AppPrimaryButton
            size={isMobileDevice ? "large" : "medium"}
            disabled={isOverlayBusy}
            onClick={handleGuidedNext}
            style={{
              color: "white",
              border: "none",
              borderColor: "transparent",
              backgroundColor: "transparent",
              minWidth: isMobileDevice ? 140 : 100,
              fontSize: isMobileDevice ? "1.1rem" : "0.95rem",
            }}
          >
            Next
          </AppPrimaryButton>
        </Box>
      )}

      {isMobileDevice ? (
        <IconButton
          style={{
            marginBottom: 20,
            position: "absolute",
            top: 20,
            left: 20,
            backgroundColor: "#00569c",
            color: "white",
            zIndex: 10,
            height: "120px",
            width: "120px",
          }}
          size={"large"}
          onClick={() => {
            const targetScene = config.scenes.find(
              (scene: IScene) => scene.id === "5",
            );
            if (targetScene) {
              setCurrentScene(targetScene);
              navigate("/virtual/5");
            }
          }}
        >
          <HomeRoundedIcon
            sx={{
              height: "90px",
              width: "90px",
            }}
          />
        </IconButton>
      ) : (
        <IconButton
          style={{
            marginBottom: 20,
            position: "absolute",
            top: 20,
            left: 20,
            backgroundColor: "#00569c",
            color: "white",
            zIndex: 10,
          }}
          size={"large"}
          onClick={() => {
            const targetScene = config.scenes.find(
              (scene: IScene) => scene.id === "5",
            );
            if (targetScene) {
              setCurrentScene(targetScene);
              navigate("/virtual/5");
            }
          }}
        >
          <HomeRoundedIcon />
        </IconButton>
      )}

      {showMovementButtons &&
        sceneMovements.map((movement: ISceneMovement, movementIndex: number) =>
          isMobileDevice ? (
            <AppPrimaryButton
              key={`${movement.target}-${movement.movementKey}-${movementIndex}`}
              style={{
                marginBottom: 20,
                position: "absolute",
                bottom: 140 + movementIndex * 130,
                right: 20,
                backgroundColor: "#00569c",
                color: "white",
                zIndex: 10,
                width: "300px",
                height: "120px",
                fontSize: "24px",
              }}
              size={"large"}
              disabled={isOverlayBusy}
              onClick={() => {
                startSceneMovement(movement);
              }}
            >
              {getMovementButtonLabel(movement, movementIndex)}
            </AppPrimaryButton>
          ) : (
            <AppPrimaryButton
              key={`${movement.target}-${movement.movementKey}-${movementIndex}`}
              style={{
                marginBottom: 20,
                position: "absolute",
                bottom: 80 + movementIndex * 60,
                right: 20,
                backgroundColor: "#00569c",
                color: "white",
                zIndex: 10,
              }}
              size={"large"}
              disabled={isOverlayBusy}
              onClick={() => {
                startSceneMovement(movement);
              }}
            >
              {getMovementButtonLabel(movement, movementIndex)}
            </AppPrimaryButton>
          ),
        )}

      {isMobileDevice ? (
        <AppPrimaryButton
          style={{
            marginBottom: 20,
            position: "absolute",
            bottom: 0,
            right: 20,
            backgroundColor: "#00569c",
            color: "white",
            zIndex: 10,
            width: "300px",
            height: "120px",
            fontSize: "30px",
          }}
          size={"large"}
          onClick={() => {
            setComingSoonOpen(true);
          }}
        >
          Feedback
        </AppPrimaryButton>
      ) : (
        <AppPrimaryButton
          style={{
            marginBottom: 20,
            position: "absolute",
            bottom: 0,
            right: 20,
            backgroundColor: "#00569c",
            color: "white",
            zIndex: 10,
          }}
          size={"large"}
          onClick={() => {
            setComingSoonOpen(true);
          }}
        >
          Feedback
        </AppPrimaryButton>
      )}
    </div>
  );
}
